import { ReactNode, Suspense } from 'react';

import { useGetCustomerState } from '@api/lendingAPI/lapiHooks';
import { tracking } from '@iwoca/frontend-tracking-library';
import { Card } from '@iwoca/orion';
import { NEW_IwLinkButton } from '@iwRouter/NEW_IwLinkButton';
import { FormattedMessage } from 'react-intl';

import { useGetDocumentFeedback } from './useGetDocumentFeedback';
import { ErrorBoundary } from '../../../../../errorBoundary/ErrorBoundary';
import { useStateKey } from '../../../../../iwQueryClient/useStateKey';
import FallbackErrorModal from '../../../../modals/fallbackErrorModal/FallbackErrorModal';
import MainContentPending from '../../../../pageStructure/mainContentPending';
import {
    mapProductStatusToStep,
    TGetProductOffersResponse
} from '../ApplicationPendingView';

type TProps = {
    suggestedProductStatus?: TGetProductOffersResponse['data']['products'][number]['status'];
    productOffersStatus: ReturnType<typeof mapProductStatusToStep>;
};

export const PendingViewActionsDE = (props: TProps) => {
    return (
        <ErrorBoundary fallback={() => <FallbackErrorModal />}>
            <Suspense
                fallback={
                    <div
                        className="col-span-2"
                        data-testid="trading-data-loading"
                    >
                        <MainContentPending />
                    </div>
                }
            >
                <PendingViewActionsDEContent {...props} />
            </Suspense>
        </ErrorBoundary>
    );
};

const PendingViewActionsDEContent = ({
    suggestedProductStatus,
    productOffersStatus
}: TProps) => {
    const { hasDocumentFeedback } = useGetDocumentFeedback();
    const isDocumentsReview = productOffersStatus === 'documentsReview';

    if (!isDocumentsReview && !hasDocumentFeedback) {
        return null;
    }

    return (
        <Card className="mt-l">
            <div className="flex w-full flex-col gap-l text-left">
                <h3>
                    <FormattedMessage
                        id="_.pending_view.quick_actions.title"
                        defaultMessage="Quick actions"
                    />
                </h3>
                {hasDocumentFeedback ? (
                    <DocumentFeedbackAction />
                ) : (
                    isDocumentsReview && (
                        <UploadDocumentsAction
                            suggestedProductStatus={suggestedProductStatus}
                        />
                    )
                )}
                {isDocumentsReview && <AddGuarantorAction />}
            </div>
        </Card>
    );
};

const DocumentFeedbackAction = () => {
    return (
        <Card className="items-left">
            <h3>
                <FormattedMessage
                    id="_.pending_view.upload_required_documents.title"
                    defaultMessage="Additional information needed"
                />
            </h3>
            <div className="text-left">
                <FormattedMessage
                    id="_.pending_view.upload_required_documents.subtitle"
                    defaultMessage="To enable us to make a decision, we need some additional information from you. Please visit the Documents page to review and action the feedback."
                />
            </div>
            <GoToTradingButton status="feedback_given">
                <FormattedMessage
                    id="_.pending_view.upload_required_documents.button"
                    defaultMessage="Go to page"
                />
            </GoToTradingButton>
        </Card>
    );
};

const UploadDocumentsAction = ({
    suggestedProductStatus
}: Pick<TProps, 'suggestedProductStatus'>) => (
    <Card className="items-left">
        <h3>
            <FormattedMessage
                id="_.pending_view.upload_documents.title"
                defaultMessage="Upload documents"
            />
        </h3>
        <div className="text-left">
            {suggestedProductStatus === 'pending_documents' && (
                <FormattedMessage
                    id="_.pending_view.upload_documents.pending_docs_message"
                    defaultMessage="If you need to upload documents, click here:"
                />
            )}
            {suggestedProductStatus === 'pending_document_review' && (
                <FormattedMessage
                    id="_.pending_view.upload_documents.pending_doc_review_message"
                    defaultMessage="If you would like to upload more documents, click here:"
                />
            )}
        </div>
        <GoToTradingButton
            status={suggestedProductStatus ?? 'undefined_status'}
        >
            <FormattedMessage
                id="_.pending_view.upload_documents.upload_button"
                defaultMessage="Go to document uploader"
            />
        </GoToTradingButton>
    </Card>
);

const DE_LEGAL_FORMS_THAT_ARENT_AUTO_LENDING_LEGIBLE: string[] = [
    'gmbh',
    'ug',
    'gmbh_u_co_kg',
    'kg',
    'ag',
    'eg'
];

const AddGuarantorAction = () => {
    const stateKey = useStateKey();
    const { data: customerStateResponse } = useGetCustomerState(stateKey);

    const isEligible = DE_LEGAL_FORMS_THAT_ARENT_AUTO_LENDING_LEGIBLE.includes(
        customerStateResponse?.application?.company?.type ?? 'no_type_found'
    );

    if (!isEligible) return null;

    return (
        <Card>
            <div className="text-left">
                <h3>
                    <FormattedMessage
                        id="_.pending_view.add_guarantor.title"
                        defaultMessage="Add an additional guarantor"
                    />
                </h3>
                <FormattedMessage
                    id="_.pending_view.add_guarantor.description"
                    defaultMessage="If you have multiple directors and want to add an additional guarantor click the button below"
                />
            </div>
            <NEW_IwLinkButton
                className="mt-l w-full min-sm:max-w-[300px]"
                to={{
                    pathname: '/company/'
                }}
            >
                <FormattedMessage
                    id="_.pending_view.add_guarantor.button"
                    defaultMessage="Go to your director list"
                />
            </NEW_IwLinkButton>
        </Card>
    );
};

const GoToTradingButton = ({
    status,
    children
}: {
    status: string;
    children: ReactNode;
}) => (
    <NEW_IwLinkButton
        className="mt-l w-full min-sm:max-w-[300px]"
        to={{ pathname: '/trading/' }}
        onClick={() =>
            tracking.ampli.uploadDocumentsCtaClicked({
                suggested_product_status: status
            })
        }
    >
        {children}
    </NEW_IwLinkButton>
);
